/* eslint-disable react/require-default-props */
import React, { useMemo, forwardRef } from 'react';
import styled from 'styled-components';
import { TypographyProps, TextDynamic } from '@minecraft.atoms';
import { ResponsiveSelectionTagsProps, SelectionButtonSize } from './types';
import { validateSelectionButtonSize, useResponsiveSelectionTagProps } from './utils';

const SIZE_CONFIG: Record<SelectionButtonSize, { diameter: string; fontSize: TypographyProps['fontSize'] }> = {
  xl: {
    diameter: '2rem',
    fontSize: 'bodySmall' as const,
  },
  lg: {
    diameter: '1.5rem',
    fontSize: 'detailLarge' as const,
  },
  sm: {
    diameter: '1.125rem',
    fontSize: 'detailRegular' as const,
  },
};

const SelectionButton = styled.button<{ $selected: boolean; $size: SelectionButtonSize }>`
  user-select: none;
  width: ${({ $size }) => SIZE_CONFIG[$size].diameter};
  height: ${({ $size }) => SIZE_CONFIG[$size].diameter};
  line-height: 0; // this makes sure the text is centered vertically in the "circle" button
  border-radius: 50%;
  margin: 0;
  padding: 0;
  outline: none;
  background-color: ${({ $selected, theme }) =>
    $selected ? theme.designSystem.bgColors.selectionTagSelected : theme.designSystem.bgColors.selectionTag};
  border: 0.0625rem solid
    ${({ $selected, theme }) =>
      $selected
        ? theme.designSystem.borderColors.statusSelectionTagSelected
        : theme.designSystem.borderColors.statusSelectionTagNotSelected};

  &&&:focus {
    outline: 2px solid
      ${({ theme, $selected }) =>
        $selected
          ? theme.designSystem.borderColors.statusSelectionTagSelectedFocus
          : theme.designSystem.borderColors.statusSelectionTagNotSelectedFocus};
  }

  @media (any-pointer: fine) {
    &:hover {
      background-color: ${({ $selected, theme }) =>
        $selected
          ? theme.designSystem.bgColors.selectionTagSelectedHover
          : theme.designSystem.bgColors.selectionTagHover};
    }
  }

  &:active {
    background-color: ${({ $selected, theme }) =>
      $selected
        ? theme.designSystem.bgColors.selectionTagSelectedActive
        : theme.designSystem.bgColors.selectionTagActive};
  }
`;

export type SelectionTagButtonProps = {
  /** whether the button renders in the selected state, or not
   * see variations in the design system
   * https://www.figma.com/design/AiDAD1WpprgfbZI2XV6hU1/Casting-Networks-Design-System?node-id=1270-14&m=dev
   * in the design system the "default" corresponds to selected = false
   * in the design system the "active" corresponds to selected = true
   */
  selected: boolean;
  /** a label for the button that is different than the normal button tag text, used for accessibility */
  'aria-label'?: string;
  /** qa id for the button */
  'data-qa-id'?: string;
  /** callback to fire when the button is clicked, this would normally trigger the selection change */
  onSelected: (newValue: string) => void;
  /** whether the button is disabled, this will prevent the button from being clickable */
  disabled?: boolean;
  /** the tag to display in the button, this is the text that will be rendered inside the button */
  tag: string;
  mdProps?: ResponsiveSelectionTagsProps;
} & ResponsiveSelectionTagsProps;

export const SelectionTagButton = forwardRef<HTMLButtonElement, SelectionTagButtonProps>(
  (
    {
      'aria-label': ariaLabel,
      'data-qa-id': dataQaId,
      disabled,
      mdProps,
      onSelected,
      selected = false,
      size: sizeProp,
      tag,
    },
    ref
  ) => {
    const { getResponsiveValues } = useResponsiveSelectionTagProps();

    const size = useMemo(() => {
      const mergedValues = getResponsiveValues({ props: { size: sizeProp }, mdProps });
      return validateSelectionButtonSize(mergedValues?.size);
    }, [getResponsiveValues, sizeProp, mdProps]);

    if (!tag || typeof tag !== 'string') {
      return null;
    }

    return (
      <SelectionButton
        $size={size}
        $selected={selected}
        aria-selected={selected}
        disabled={typeof onSelected !== 'function' || disabled}
        key={tag}
        onClick={() => onSelected(tag)}
        type="button"
        data-qa-id={dataQaId}
        ref={ref}
        aria-label={ariaLabel || undefined}
      >
        <TextDynamic
          align="center"
          color={selected ? 'inverted' : 'link'}
          fontWeight="semibold"
          lineHeight="ultraCondensed"
          fontSize={SIZE_CONFIG[size].fontSize}
        >
          {tag}
        </TextDynamic>
      </SelectionButton>
    );
  }
);
